import GButton from "../../components/ui/base/GButton";
import useMainStore from "../../store/mainState";
import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_USER } from "../../graphql/user";
import { useTranslation } from 'react-i18next';

const avatarList = [
    require("../../assets/images/avatar.png"),
    require("../../assets/images/avatar1.png"),
    require("../../assets/images/avatar2.png"),
    require("../../assets/images/avatar3.png"),
    require("../../assets/images/avatar4.png"),
    require("../../assets/images/avatar5.png"),
    require("../../assets/images/avatar6.png"),
    require("../../assets/images/avatar7.png"),
    require("../../assets/images/avatar8.png"),
];

const Profile = () => {
    const { user, setIsLoading } = useMainStore();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = useState(false);
    const [name, setName] = useState(user?.username);
    const [email, setEmail] = useState(user?.email);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [selectedAvatar, setSelectedAvatar] = useState(0);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [updateUser, { loading, error }] = useMutation(UPDATE_USER, { errorPolicy: 'all' });

    const balance = user?.Balance ? (user.Balance.amount + user.Balance.onHold) : 0;

    const onUpdate = async () => {
        if (password && password !== confirmPassword) {
            setMessage({ type: 'error', text: t('profile.passwordsMismatch') });
            return;
        }
        setMessage(null);
        try {
            setIsLoading(true);
            const variables: any = { id: user?.id };
            if (name !== user?.username) variables.username = name;
            if (email !== user?.email) variables.email = email;
            if (password) variables.password = password;

            const result = await updateUser({ variables });
            if (result.errors && result.errors.length > 0) {
                setMessage({ type: 'error', text: result.errors[0].message });
            } else {
                setMessage({ type: 'success', text: t('profile.profileUpdated') });
                setIsEditing(false);
                setPassword("");
                setConfirmPassword("");
            }
            setIsLoading(false);
        } catch (error: any) {
            setMessage({ type: 'error', text: error?.message || t('profile.updateFailed') });
            setIsLoading(false);
        }
    }

    useEffect(() => {
        setName(user?.username);
        setEmail(user?.email);
    }, [user]);

    return (
        <div className="pb-[40px] max-w-[700px]">
            {/* Header */}
            <h1 className="text-[28px] md:text-[36px] font-extrabold text-white tracking-tight mb-[30px]">{t('profile.title')}</h1>

            {/* Profile Card */}
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] overflow-hidden mb-[24px]">
                {/* Banner */}
                <div className="h-[100px] relative overflow-hidden">
                    <img src={require("../../assets/images/bannerBg.png")} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0a0e1a]/80"></div>
                </div>

                {/* Avatar + Info */}
                <div className="px-[24px] pb-[24px] relative">
                    <div className="flex flex-col sm:flex-row sm:items-end gap-[16px] -mt-[40px]">
                        <div className="relative">
                            <img
                                src={avatarList[selectedAvatar]}
                                alt="Avatar"
                                className="w-[80px] h-[80px] rounded-full border-[3px] border-[#0a0e1a] object-cover shadow-[0_4px_20px_rgba(0,0,0,0.4)]"
                            />
                            <div className="absolute bottom-[2px] right-[2px] w-[14px] h-[14px] rounded-full bg-emerald-400 border-2 border-[#0a0e1a]"></div>
                        </div>
                        <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[10px]">
                            <div>
                                <h2 className="text-white text-[22px] font-bold">{user?.username}</h2>
                                <p className="text-white/30 text-[13px] font-medium">{user?.email || t('common.noEmailSet')}</p>
                            </div>
                            <div className="flex items-center gap-[12px]">
                                <div className="bg-white/[0.06] border border-white/[0.08] rounded-[12px] px-[16px] py-[10px]">
                                    <p className="text-white/30 text-[10px] font-semibold uppercase tracking-wider">{t('common.balance')}</p>
                                    <p className="text-[#4DBDFF] text-[20px] font-bold">${balance.toFixed(2)}</p>
                                </div>
                                <GButton
                                    variant={isEditing ? "secondary" : "primary"}
                                    className="py-[10px] px-[20px] rounded-[12px]"
                                    onClick={() => { setIsEditing(!isEditing); setMessage(null); }}
                                >
                                    {isEditing ? t('common.cancel') : t('common.edit')}
                                </GButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message */}
            {message && (
                <div className={`mb-[20px] px-4 py-3 rounded-[12px] text-[14px] font-semibold animate-[fadeIn_0.3s_ease-out] ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                    {message.text}
                </div>
            )}

            {/* Form */}
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[24px] mb-[24px]">
                <h3 className="text-white text-[16px] font-bold mb-[20px]">{t('profile.accountDetails')}</h3>

                <div className="space-y-[20px]">
                    <div>
                        <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">{t('profile.username')}</label>
                        <input
                            onChange={(e) => setName(e.target.value)}
                            type="text"
                            value={name || ''}
                            disabled={!isEditing}
                            className={`w-full px-[16px] py-[12px] rounded-[12px] text-white text-[15px] font-medium outline-none transition-all duration-300 ${isEditing ? 'bg-white/[0.06] border border-white/[0.1] focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)]' : 'bg-white/[0.03] border border-white/[0.04] text-white/60'}`}
                        />
                    </div>
                    <div>
                        <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">{t('profile.email')}</label>
                        <input
                            onChange={(e) => setEmail(e.target.value)}
                            type="text"
                            value={email || ''}
                            disabled={!isEditing}
                            className={`w-full px-[16px] py-[12px] rounded-[12px] text-white text-[15px] font-medium outline-none transition-all duration-300 ${isEditing ? 'bg-white/[0.06] border border-white/[0.1] focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)]' : 'bg-white/[0.03] border border-white/[0.04] text-white/60'}`}
                        />
                    </div>

                    {isEditing && (
                        <>
                            <div>
                                <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">{t('profile.newPassword')}</label>
                                <input
                                    onChange={(e) => { setPassword(e.target.value); setMessage(null); }}
                                    placeholder={t('profile.leaveEmpty')}
                                    type="password"
                                    value={password}
                                    className="w-full px-[16px] py-[12px] rounded-[12px] bg-white/[0.06] border border-white/[0.1] text-white text-[15px] font-medium outline-none placeholder-white/20 focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)] transition-all duration-300"
                                />
                            </div>
                            <div>
                                <label className="block text-white/40 text-[12px] font-semibold uppercase tracking-widest mb-[8px]">{t('profile.confirmPassword')}</label>
                                <input
                                    onChange={(e) => { setConfirmPassword(e.target.value); setMessage(null); }}
                                    placeholder={t('profile.repeatPassword')}
                                    type="password"
                                    value={confirmPassword}
                                    className={`w-full px-[16px] py-[12px] rounded-[12px] bg-white/[0.06] border text-white text-[15px] font-medium outline-none placeholder-white/20 transition-all duration-300 ${password && confirmPassword && password !== confirmPassword ? 'border-red-500/50 focus:border-red-500/70' : 'border-white/[0.1] focus:border-[#4DBDFF]/50 focus:shadow-[0_0_15px_rgba(0,134,211,0.1)]'}`}
                                />
                                {password && confirmPassword && password !== confirmPassword && (
                                    <p className="text-red-400 text-[12px] font-semibold mt-[6px]">{t('profile.passwordsMismatch')}</p>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Avatar Selection */}
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[24px] mb-[24px]">
                <h3 className="text-white text-[16px] font-bold mb-[4px]">{t('profile.avatar')}</h3>
                <p className="text-white/30 text-[13px] mb-[16px]">{t('profile.selectAvatar')}</p>
                <div className="flex flex-wrap gap-[10px]">
                    {avatarList.map((avatar, index) => (
                        <button
                            key={index}
                            onClick={() => setSelectedAvatar(index)}
                            className={`w-[52px] h-[52px] rounded-full overflow-hidden border-[2px] transition-all duration-300 ${selectedAvatar === index ? 'border-[#4DBDFF] shadow-[0_0_15px_rgba(0,134,211,0.3)] scale-110' : 'border-white/[0.08] hover:border-white/[0.2]'}`}
                        >
                            <img src={avatar} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            </div>

            {/* Save Button */}
            {isEditing && (
                <GButton
                    variant="primary"
                    className="w-full py-[14px] rounded-[14px]"
                    onClick={onUpdate}
                    disabled={loading}
                >
                    {loading ? t('common.saving') : t('common.save')}
                </GButton>
            )}
        </div>
    )
}

export default Profile
